import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Business } from "@dropin/shared/schemas";
import { getMyBusinesses, logout } from "../api";
import { useUser } from "../hooks/useUser";
import { BusinessDashboard } from "../components/BusinessDashboard";
import { CreatorDashboard } from "../components/CreatorDashboard";
import { BusinessSetup } from "../components/BusinessSetup";
import { AdminDashboard } from "../components/AdminDashboard";

export function DashboardPage() {
  const { user, loading: userLoading, error, setUser } = useUser();
  const [businesses, setBusinesses] = useState<Business[] | null>(null);
  const [bizLoading, setBizLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getMyBusinesses()
      .then((r) => setBusinesses(r.businesses))
      .catch(() => setBusinesses([]))
      .finally(() => setBizLoading(false));
  }, [user]);

  if (error) {
    return (
      <div className="card max-w-md">
        <h1 className="text-xl font-semibold tracking-tightish">Not signed in</h1>
        <p className="mt-1 text-sm text-muted">Sign in to access your dashboard.</p>
        <Link to="/signin" className="btn-primary mt-4 inline-flex" data-testid="cta-signin">
          Sign in
        </Link>
      </div>
    );
  }

  if (userLoading || !user || bizLoading || businesses === null) {
    return <div className="card text-muted animate-fade-in">Loading…</div>;
  }

  const isAdmin = user.role === "admin";
  const isBusinessIntent = user.role === "business";
  const hasBusiness = businesses.length > 0;

  return (
    <div className="grid gap-5">
      {isAdmin ? (
        <AdminDashboard />
      ) : hasBusiness ? (
        <BusinessDashboard
          business={businesses[0]!}
          onBusinessUpdated={(b) =>
            setBusinesses((prev) =>
              prev ? prev.map((x) => (x.id === b.id ? b : x)) : [b],
            )
          }
        />
      ) : isBusinessIntent ? (
        <BusinessSetup
          onCreated={(b) => setBusinesses([b])}
        />
      ) : (
        <CreatorDashboard user={user} onUserUpdated={setUser} />
      )}

      {/* Account chrome — secondary, lives at the bottom */}
      <div className="card flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <span className="chip" data-testid="user-role">
            {user.role}
          </span>
          <h1
            className="mt-2 text-xl font-semibold tracking-tightish truncate"
            data-testid="user-email"
          >
            {user.email}
          </h1>
        </div>
        <button
          className="btn-ghost text-sm"
          onClick={() => logout().then(() => location.assign("/"))}
          data-testid="signout"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
